// src/components/admin/UserManagement.js
import React, { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  doc, 
  updateDoc, 
  query, 
  orderBy, 
  limit, 
  startAfter, 
  where,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../../firebase';
import Loading from './Loading';
import ErrorBoundary from './ErrorBoundary';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingRoleChange, setPendingRoleChange] = useState(null);
  
  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);
  
  // Function to fetch users
  const fetchUsers = async (loadMore = false) => {
    try {
      setLoading(true);
      setError(null);
      
      // Create a reference to the users collection
      const usersRef = collection(db, 'users');
      
      // Create a query to get users ordered by createdAt
      let usersQuery;
      if (loadMore && lastDoc) {
        usersQuery = query(usersRef, orderBy('createdAt', 'desc'), startAfter(lastDoc), limit(10));
      } else {
        usersQuery = query(usersRef, orderBy('createdAt', 'desc'), limit(10));
      }
      
      // Execute the query
      const querySnapshot = await getDocs(usersQuery);
      
      // Check if we've reached the end
      setHasMore(querySnapshot.size === 10);
      
      // Update lastDoc for pagination
      if (querySnapshot.size > 0) {
        setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
      } else {
        setLastDoc(null);
      }
      
      // Process the results
      const fetchedUsers = [];
      querySnapshot.forEach((doc) => {
        // Format createdAt date if it exists
        let createdAt = 'Unknown';
        if (doc.data().createdAt) {
          const timestamp = doc.data().createdAt;
          if (timestamp instanceof Timestamp) {
            const date = timestamp.toDate();
            createdAt = date.toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit'
            }).replace(',', '');
          }
        }
        
        fetchedUsers.push({
          id: doc.id,
          ...doc.data(),
          createdAt
        });
      });
      
      // Update state based on whether this is initial load or loading more
      if (loadMore) {
        setUsers(prevUsers => [...prevUsers, ...fetchedUsers]);
      } else {
        setUsers(fetchedUsers);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Failed to load users: " + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Function to handle role change initiation
  const handleRoleChange = (userId, newRole) => {
    // Store the pending change
    setPendingRoleChange({
      userId,
      isAdmin: newRole === 'admin'
    });
    
    // If changing to a more privileged role, show confirmation
    if (newRole === 'admin') {
      setShowConfirmation(true);
    } else {
      // For downgrading, apply immediately
      applyRoleChange();
    }
  };
  
  // Function to apply the role change after confirmation
  const applyRoleChange = async () => {
    if (!pendingRoleChange) return;
    
    const { userId, isAdmin } = pendingRoleChange;
    
    try {
      setLoading(true);
      
      // Update the user document
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        isAdmin: isAdmin
      });
      
      // Update local state
      setUsers(users.map(user => 
        user.id === userId ? {...user, isAdmin: isAdmin} : user
      ));
      
      // Clear confirmation dialog
      setShowConfirmation(false);
      setPendingRoleChange(null);
    } catch (err) {
      console.error("Error updating user role:", err);
      setError("Failed to update user role: " + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Function to cancel a pending role change
  const cancelRoleChange = () => {
    setShowConfirmation(false);
    setPendingRoleChange(null);
  };
  
  // Load more users
  const handleLoadMore = () => {
    if (hasMore && !loading) {
      fetchUsers(true);
    }
  };
  
  // Format date if needed
  const formatDate = (dateString) => {
    return dateString;
  };
  
  // Count total admins
  const adminCount = users.filter(user => user.isAdmin).length;
  
  return (
    <div className="space-y-6 bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
        <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">User Management</h2>
        <p className="text-gray-600 dark:text-gray-300">
          Manage user roles and permissions. Admins have access to the admin dashboard and can modify all data.
        </p>
      </div>
      
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 text-red-800 dark:text-red-400 p-4 rounded-md flex justify-between items-center">
          <p>{error}</p>
          <button 
            onClick={() => setError(null)}
            className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-800/30 focus:outline-none"
          >
            Dismiss
          </button>
        </div>
      )}
      
      <div className="flex flex-wrap gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <div className="flex items-center gap-2">
          <span className="text-gray-700 dark:text-gray-300 font-medium">Total Users:</span>
          <span className="font-bold text-gray-900 dark:text-gray-100">{users.length}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-gray-700 dark:text-gray-300 font-medium">Admins:</span>
          <span className="font-bold text-gray-900 dark:text-gray-100">{adminCount}</span>
        </div>
      </div>
      
      <ErrorBoundary>
        <div className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
                  <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Display Name</th>
                  <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created At</th>
                  <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Admin Status</th>
                  <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                {users.map(user => (
                  <tr key={user.id} className={user.isAdmin ? "bg-blue-50 dark:bg-blue-900/20" : "hover:bg-gray-50 dark:hover:bg-gray-700/50"}>
                    <td className="p-4 text-sm text-gray-900 dark:text-gray-200">{user.email}</td>
                    <td className="p-4 text-sm text-gray-900 dark:text-gray-200">{user.displayName || 'N/A'}</td>
                    <td className="p-4 text-sm text-gray-900 dark:text-gray-200">{formatDate(user.createdAt)}</td>
                    <td className="p-4 text-sm text-gray-900 dark:text-gray-200">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        user.isAdmin 
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' 
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        {user.isAdmin ? 'Admin' : 'User'}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-gray-900 dark:text-gray-200">
                      <select 
                        className="p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={user.isAdmin ? 'admin' : 'user'}
                        onChange={(e) => handleRoleChange(user.id, e.target.value)}
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                  </tr>
                ))}
                
                {users.length === 0 && !loading && (
                  <tr>
                    <td colSpan="5" className="p-4 text-center text-gray-500 dark:text-gray-400">
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            
            {loading && <div className="p-6"><Loading /></div>}
            
            {hasMore && !loading && (
              <div className="flex justify-center p-4">
                <button 
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors"
                  onClick={handleLoadMore}
                >
                  Load More Users
                </button>
              </div>
            )}
            
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300">
              Showing {users.length} user(s)
              {adminCount > 0 && ` (${adminCount} admins)`}
            </div>
          </div>
        </div>
      </ErrorBoundary>
      
      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-bold text-gray-800 dark:text-white">Confirm Role Change</h2>
            </div>
            <div className="p-4">
              <p className="text-gray-700 dark:text-gray-300">
                Are you sure you want to grant admin privileges to this user? 
                Admins have full access to all data and can manage other users.
              </p>
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button 
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded transition-colors"
                onClick={cancelRoleChange}
              >
                Cancel
              </button>
              <button 
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors"
                onClick={applyRoleChange}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;